import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {API} from "api";
import {useAuth} from "hooks/useAuth";
import {isRenderTimelineControlled} from "libs/renderTimeline";
import {
  resolveFeatures,
  type FeatureKey,
  type FeatureTargetsDoc,
} from "data/features";

/**
 * The feature_flags list (GET /api/flags). Admin → Feature Flags reads the
 * same query, so a target that an admin changes reaches the editor on the
 * refetch after the change.
 */
export const FEATURE_FLAGS_QUERY_KEY = ["feature-flags"] as const;

export async function fetchFeatureFlagDocs(): Promise<FeatureTargetsDoc[]> {
  const docs = await API.fetchFeatureFlags();
  if (!Array.isArray(docs)) {
    throw new Error("Invalid /api/flags response: a list was expected");
  }
  return docs;
}

export const featureFlagsQuery = {
  queryKey: FEATURE_FLAGS_QUERY_KEY,
  queryFn: fetchFeatureFlagDocs,
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};

/**
 * Every registered feature (data/features.ts) for the current role. The role
 * is the effective role, so "View as role" shows the features of that role.
 *
 * Until the list loads, or when it cannot load, each role has the defaults:
 * the behaviour from before the feature existed. The failed request is
 * logged by api.js.
 *
 * The renderer (exports) has no features. A feature only shows or hides an
 * editor control, so the export is the same; and no export waits for the
 * list or changes when an admin changes a target.
 */
export function useFeatures(): Record<FeatureKey, boolean> {
  const {role} = useAuth();
  const renderer = isRenderTimelineControlled();
  const {data} = useQuery({...featureFlagsQuery, enabled: !renderer});
  return useMemo(() => resolveFeatures(renderer ? null : role, data), [renderer, role, data]);
}

/** Has the current role the feature? See useFeatures. */
export function useFeature(key: FeatureKey): boolean {
  return useFeatures()[key];
}
